import * as React from "react";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import {
  Modal,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTextContent,
  DialogLinkContent,
  Alert
} from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import cx from "classnames";
import { QCmd } from "@coreapp/CoreApp/QCmd";
import { AttachCmdToObj } from "@coreapp/utils/AttachCmdToObj";
import { BannerErrorAlert } from "./BannerErrorAlert";
import FocusTrap from "focus-trap-react";
import { cardFocusTrapOptions } from "@/appComponents/Blocks/Cards/CardWrapper/CardWrapper";

/**
The onClick event handler is triggered when the user clicks the "Close" button.
The processDialogQueue function from the WorkspaceContext is invoked.
Inside the WorkspaceManager component, the setAlertQueueState function is called with the previous state as an argument.
The setAlertQueueState function updates the state by creating a new state object using the spread operator (...prevState).
In the new state object, the dialogQueue property is updated by using the slice method to remove the first dialog from the queue. This ensures that the queue is updated and the first dialog is removed.
The dialogQueue in the state is updated, triggering a re-render of the components that consume the WorkspaceContext.
Since the Dialog component is consuming the WorkspaceContext, it receives the updated dialogQueue through the context re-render.
If there are more dialogs in the updated dialogQueue, the new first dialog is rendered. If there are no more dialogs, the return null statement inside the if (!currentDialog) block is executed, and the Dialog component returns null, indicating that no dialog should be displayed.
In summary, when processDialogQueue is called, it removes the current dialog from the queue and triggers a re-render of the Dialog component to display the next dialog, if available, or hide the dialog if the queue is empty.
 */

const Dialog = ({
  show,
  dividers,
  title,
  message,
  onClose,
  confirm,
  deny,
  dismiss,
  tertiary,
  helpLink,
  alertProps,
  eventData,
  minWidth,
  maxWidth,
  id,
  restrictDismiss,
  secondaryBtnProps,
  primaryBtnProps
}) => {
  const [isSubmitting, setSubmitting] = React.useState(false);
  const modalId = id ?? "alert-modal";
  const confirmTestId = primaryBtnProps?.dataTestId ?? "dialog-confirm-btn";
  const focusTrapOptions = {
    ...cardFocusTrapOptions,
    initialFocus: () =>
      document.getElementById(modalId)?.querySelector(`[data-testid="${confirmTestId}"]`) ??
      undefined
  };

  const handleOCDialog = action => {
    let wsMgr = StateLib.GetWSMgr();
    let cmd = new QCmd();
    cmd.fWSMgr = wsMgr;
    cmd.fCmdCode = action?.code;
    cmd.fSourcePanel = eventData?.ref;
    if (action?.params) {
      AttachCmdToObj(cmd, action.params);
    }
    wsMgr.DoExecuteCommand(cmd);
    onClose();
  };

  const handleCustomDialog = onClick => async () => {
    await onClick?.();
    onClose();
  };

  const handleActionClick = onClick => async e => {
    setSubmitting(true);
    await onClick?.();
    setSubmitting(false);
  };

  return (
    <CentralizedModalContainer show={show}>
      <FocusTrap focusTrapOptions={focusTrapOptions}>
        <Modal
          id={modalId}
          show={show}
          onClose={restrictDismiss ? undefined : handleCustomDialog(dismiss?.onDismiss)}
          minWidth={minWidth}
          maxWidth={maxWidth}
          customStyle={{ zIndex: 1000 }}
        >
          <DialogTitle
            onClose={restrictDismiss ? undefined : handleCustomDialog(dismiss?.onDismiss)}
          >
            {title}
          </DialogTitle>
          <DialogContent dividers={dividers}>
            {alertProps && (
              <Alert className={cx("oc-padding-small", "oc-text-small")} {...alertProps}>
                {alertProps.message}
              </Alert>
            )}
            <DialogTextContent>{message}</DialogTextContent>
            {helpLink &&
              (helpLink.href ? (
                <div className="oc-dialog-help-link">
                  <DialogLinkContent
                    underline={true}
                    linkName={helpLink.label}
                    url={helpLink.href} // helpLink.code TODO add handling for code => url
                  />
                </div>
              ) : (
                <DialogTextContent>{helpLink.label}</DialogTextContent>
              ))}
          </DialogContent>
          <DialogActions size="medium">
            {tertiary && (
              <DialogActions.Tertiary
                className="oc-left-justify"
                dataTestId={"dialog-tertiary-btn"}
                loading={isSubmitting}
                onClick={handleActionClick(
                  tertiary.customOnClick
                    ? handleCustomDialog(tertiary.customOnClick)
                    : () => handleOCDialog(tertiary)
                )}
              >
                {tertiary.label}
              </DialogActions.Tertiary>
            )}
            {deny && (
              <DialogActions.Secondary
                {...secondaryBtnProps}
                dataTestId={"dialog-deny-btn"}
                loading={isSubmitting}
                onClick={handleActionClick(
                  deny.customOnClick
                    ? handleCustomDialog(deny.customOnClick)
                    : () => handleOCDialog(deny)
                )}
              >
                {deny.label}
              </DialogActions.Secondary>
            )}
            <DialogActions.Primary
              {...primaryBtnProps}
              dataTestId={confirmTestId}
              loading={isSubmitting}
              onClick={handleActionClick(
                confirm.customOnClick
                  ? handleCustomDialog(confirm.customOnClick)
                  : () => handleOCDialog(confirm)
              )}
            >
              {confirm.label}
            </DialogActions.Primary>
          </DialogActions>
        </Modal>
      </FocusTrap>
    </CentralizedModalContainer>
  );
};

export { BannerErrorAlert };

export const DialogManager = () => {
  const {
    alertContextValue: { alertQueue, processAlertQueue }
  } = useWSMgrContext();
  if (!alertQueue) return;

  const currentDialog = alertQueue[0];

  if (!currentDialog) {
    return null; // No dialog to display
  }

  if (currentDialog.bannerErrorAlert) {
    return (
      <BannerErrorAlert
        title={currentDialog.title}
        message={currentDialog.message}
        onClose={processAlertQueue}
        confirm={currentDialog.confirm}
        deny={currentDialog.deny}
      />
    );
  }

  return (
    <Dialog
      show={true}
      eventData={currentDialog}
      onClose={processAlertQueue}
      minWidth={"small"}
      maxWidth={"medium"}
      {...currentDialog}
    />
  );
};

export default Dialog;
